import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  Timestamp,
  FieldValue,
  where
} from 'firebase/firestore';
import { db } from './firebase';
import type { Visitation, VisitationFormData } from '../types/visitation';

const visitationsCollectionRef = collection(db, 'visitations');

export const visitationService = {
  async getAllVisitations(): Promise<Visitation[]> {
    const q = query(visitationsCollectionRef, orderBy('visitDate', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        visitDate: data.visitDate as Timestamp,
        createdAt: data.createdAt as Timestamp,
        updatedAt: data.updatedAt ? data.updatedAt as Timestamp : undefined,
      } as Visitation;
    });
  },

  async getVisitationById(id: string): Promise<Visitation | null> {
    const docRef = doc(db, 'visitations', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        visitDate: data.visitDate as Timestamp,
        createdAt: data.createdAt as Timestamp,
        updatedAt: data.updatedAt ? data.updatedAt as Timestamp : undefined,
      } as Visitation;
    } else {
      console.warn(`Visitation with ID ${id} not found.`);
      return null;
    }
  },

  async addVisitation(visitationData: VisitationFormData): Promise<string> {
    const dataToSave: Omit<Visitation, 'id' | 'createdAt' | 'updatedAt' | 'visitDate'> & { visitDate: Timestamp, createdAt: FieldValue, updatedAt?: FieldValue } = {
      memberId: visitationData.memberId,
      memberName: visitationData.memberName,
      visitDate: Timestamp.fromDate(new Date(visitationData.visitDate)),
      visitors: visitationData.visitors.split(',').map(v => v.trim()).filter(v => v), // Handle single or multiple, trim whitespace
      type: visitationData.type,
      status: visitationData.status,
      notes: visitationData.notes,
      createdAt: serverTimestamp(),
    };

    if (visitationData.followUpActions) {
      dataToSave.followUpActions = visitationData.followUpActions;
    }

    const docRef = await addDoc(visitationsCollectionRef, dataToSave);
    return docRef.id;
  },

  async updateVisitation(id: string, updates: Partial<VisitationFormData>): Promise<void> {
    const dataToUpdate: Partial<Omit<Visitation, 'id' | 'createdAt' | 'updatedAt' | 'visitDate'> & { visitDate?: Timestamp, updatedAt?: FieldValue }> = {};

    if (updates.memberId) dataToUpdate.memberId = updates.memberId;
    if (updates.memberName) dataToUpdate.memberName = updates.memberName;
    if (updates.visitDate) dataToUpdate.visitDate = Timestamp.fromDate(new Date(updates.visitDate));
    if (updates.visitors) dataToUpdate.visitors = updates.visitors.split(',').map(v => v.trim()).filter(v => v);
    if (updates.type) dataToUpdate.type = updates.type;
    if (updates.status) dataToUpdate.status = updates.status;
    if (updates.notes) dataToUpdate.notes = updates.notes;
    if (updates.hasOwnProperty('followUpActions')) dataToUpdate.followUpActions = updates.followUpActions;

    if (Object.keys(dataToUpdate).length > 0) {
      dataToUpdate.updatedAt = serverTimestamp();
      const docRef = doc(db, 'visitations', id);
      await updateDoc(docRef, dataToUpdate);
    } else {
      console.log("No actual changes to update for visitation: ", id);
    }
  },

  async deleteVisitation(id: string): Promise<void> {
    const docRef = doc(db, 'visitations', id);
    await deleteDoc(docRef);
  },

  async getVisitationsForMember(memberId: string): Promise<Visitation[]> {
    try {
      const q = query(
        visitationsCollectionRef,
        where('memberId', '==', memberId),
        orderBy('visitDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          visitDate: data.visitDate as Timestamp,
          createdAt: data.createdAt as Timestamp,
          updatedAt: data.updatedAt ? data.updatedAt as Timestamp : undefined,
        } as Visitation;
      });
    } catch (error) {
      console.error('심방 기록 조회 중 오류 발생:', error);
      // 인덱스 오류인 경우 사용자에게 안내
      if (error instanceof Error && error.message?.includes('requires an index')) {
        console.warn('Firestore 인덱스가 필요합니다. Firebase 콘솔에서 인덱스를 생성해주세요.');
      }
      return [];
    }
  },
};
